import React, { useEffect, useState, useCallback } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaUserCheck } from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';
import './RequiredQualificationsPanel.css';

// Fetches the gate evaluation for the given job and renders an inline panel
// showing every requirement, which staff cover it, and whether the quote can
// be submitted. The parent quote form should respect `onGateChange(satisfied)`
// to enable/disable its Submit button.
//
// Also exposes the contractor's eligible staff so the parent can render an
// "Assign staff" step. We keep that as a separate component below; this one
// just shows the requirements transparently.
const RequiredQualificationsPanel = ({ jobId, onGateChange, onAssignmentDataChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchGate = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const r = await fetch(getApiEndpoint(`/api/jobs/${jobId}/quote-gate`), {
        headers: getAuthHeader(),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setData(d);
      if (typeof onGateChange === 'function') onGateChange(d.satisfied !== false);
      if (typeof onAssignmentDataChange === 'function') onAssignmentDataChange(d.requirements || []);
    } catch (_) {
      setData({ hasRequirements: false, satisfied: true, requirements: [] });
      if (typeof onGateChange === 'function') onGateChange(true);
    } finally { setLoading(false); }
  }, [jobId, onGateChange, onAssignmentDataChange]);

  useEffect(() => { fetchGate(); }, [fetchGate]);

  if (loading) return <div className="cs-rqp-state">Checking job requirements…</div>;
  if (!data || !data.hasRequirements) return null;

  return (
    <div className={`cs-rqp ${data.satisfied ? 'satisfied' : 'unsatisfied'}`}>
      <div className="cs-rqp-header">
        {data.satisfied ? (
          <>
            <FaCheckCircle className="cs-rqp-icon ok" />
            <span><strong>Your team meets every requirement.</strong> You can submit a quote.</span>
          </>
        ) : (
          <>
            <FaExclamationTriangle className="cs-rqp-icon warn" />
            <span><strong>Your team is missing required qualifications.</strong> You can't submit a quote until these are met.</span>
          </>
        )}
      </div>
      <ul className="cs-rqp-list">
        {data.requirements.map(r => (
          <li key={r.qualification_id} className={`cs-rqp-row ${r.met ? 'met' : 'missing'}`}>
            <span className="cs-rqp-name">
              <strong>{r.name}</strong>
              {r.category && <span className="cs-rqp-cat"> · {r.category}</span>}
            </span>
            <span className="cs-rqp-count">
              {r.eligible_staff.length} / {r.min_count}
              {r.met ? <FaCheckCircle className="cs-rqp-tick" /> : <FaExclamationTriangle className="cs-rqp-warn" />}
            </span>
            {r.eligible_staff.length > 0 && (
              <span className="cs-rqp-staff">
                <FaUserCheck /> {r.eligible_staff.map(s => s.name).join(', ')}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RequiredQualificationsPanel;
